// Simplified CustomerReviews.js component
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Button } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight, FaStar, FaQuoteLeft } from 'react-icons/fa';
import api from '../services/api';


const CustomerReviews = () => {
    // Single default review to use as fallback
    const defaultReview = {
        id: 1,
        name: "Ahmet Yılmaz",
        location: "İstanbul",
        rating: 5,
        text: "Otoprime sayesinde hayalimdeki arabaya kolayca sahip oldum. Tüm süreç çok profesyonel ve şeffaf ilerledi. Aylık ödemelerim bütçeme uygun, kesinlikle tavsiye ediyorum!"
    };

    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Fetch reviews from API or use default
    useEffect(() => {
        const fetchReviews = async () => {
            try {
                setLoading(true);
                const response = await api.get('/reviews', {
                    timeout: 3000 // 3 second timeout
                });

                if (response.data && response.data.reviews && response.data.reviews.length > 0) {
                    setReviews(response.data.reviews);
                } else {
                    // Use default review if no data from API
                    setReviews([defaultReview]);
                }
            } catch (err) {
                console.error('Error fetching reviews:', err);
                // Use default review if API fails
                setReviews([defaultReview]);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, []);

    // Navigation functions
    const goPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            // Loop to last page
            setCurrentIndex(Math.ceil(reviews.length / 2) - 1);
        }
    };

    const goNext = () => {
        if (currentIndex < Math.ceil(reviews.length / 2) - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            // Loop to first page
            setCurrentIndex(0);
        }
    };

    // Loading state
    if (loading) {
        return (
            <Container className="py-5 text-center">
                <h2 className="section-title text-center mb-5">Müşteri Yorumları</h2>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    // Show 2 reviews per page
    const itemsPerPage = 2;
    const startIdx = currentIndex * itemsPerPage;
    const visibleReviews = reviews.slice(startIdx, startIdx + itemsPerPage);
    const totalPages = Math.ceil(reviews.length / itemsPerPage);

    // Star rating helper function
    const renderStars = (rating) => {
        return (
            <div className="d-flex justify-content-center mb-3">
                {[...Array(5)].map((_, i) => (
                    <FaStar
                        key={i}
                        className={i < rating ? "text-warning me-1" : "text-muted me-1"}
                    />
                ))}
            </div>
        );
    };

    return (
        <section className="py-5 bg-light">
            <Container>
                <h2 className="section-title text-center mb-5">Müşteri Yorumları</h2>
                <p className="lead text-center mb-5">Otoprime'ı tercih eden müşterilerimizin deneyimleri</p>

                <Row className="justify-content-center">
                    <Col lg={10}>
                        <Row className="g-4">
                            {visibleReviews.map(review => (
                                <Col md={6} key={review.id || review._id}>
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="p-4 text-center">
                                            <FaQuoteLeft className="text-muted mb-3" size={24} />
                                            <p className="mb-4 fst-italic">"{review.text}"</p>
                                            {renderStars(review.rating)}
                                            <h5 className="fw-bold mb-1">{review.name}</h5>
                                            <p className="text-muted small mb-0">{review.location}</p>
                                            {review.createdAt && (
                                                <p className="text-muted small">
                                                    {new Date(review.createdAt).toLocaleDateString('tr-TR')}
                                                </p>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Navigation Controls - Centered */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center mt-4 gap-3">
                                <Button
                                    variant="outline-dark"
                                    size="sm"
                                    onClick={goPrevious}
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: '40px', height: '40px' }}
                                    aria-label="Önceki yorumlar"
                                >
                                    <FaChevronLeft />
                                </Button>

                                {/* Page Indicators */}
                                <div className="d-flex gap-2">
                                    {Array.from({ length: totalPages }).map((_, index) => (
                                        <button
                                            key={index}
                                            className={`btn btn-sm rounded-circle ${index === currentIndex ? 'btn-dark' : 'btn-outline-secondary'
                                                }`}
                                            style={{ width: '8px', height: '8px', padding: 0 }}
                                            onClick={() => setCurrentIndex(index)}
                                            aria-label={`Sayfa ${index + 1}`}
                                        />
                                    ))}
                                </div>

                                <Button
                                    variant="outline-dark"
                                    size="sm"
                                    onClick={goNext}
                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: '40px', height: '40px' }}
                                    aria-label="Sonraki yorumlar"
                                >
                                    <FaChevronRight />
                                </Button>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default CustomerReviews;